import { z } from 'zod';
import { Uuid } from './common.js';

/**
 * **Unmerge** — taking one `entity_merge` back
 * (`docs/notes/2026-10-02-merge-triage-and-unmerge-spec.md` §2).
 *
 * `POST /orgs/:orgId/merges/:decisionId/unmerge` answers with
 * {@link UnmergeResult}; the operator's `entity:unmerge` prints the same thing.
 * A refusal is a 409 `UNMERGE_REFUSED` whose `details.reason` is one of
 * {@link UNMERGE_REFUSAL_REASONS}, and whose message is the sentence.
 */

/**
 * **Why an unmerge refused.** Each is a different next move for the reader:
 *
 *  - `not_found` — the decision is not an `entity_merge` of this organization.
 *  - `already_reversed` — it has been unmerged already.
 *  - `chained` — the surviving record was itself merged into a third one since.
 *    Chains unwind in reverse order, so that later merge is undone first.
 *  - `in_flight_run` — research is running on the surviving record. A wait.
 *  - `name_taken` — another live record now goes by a name this would bring
 *    back. Two live records of one name is not a state an unmerge should create.
 *  - `record_changed` — one of the two records is no longer in the state the
 *    merge left it in (deleted, or changed while this ran). Nothing is guessed.
 */
export const UNMERGE_REFUSAL_REASONS = [
  'not_found',
  'already_reversed',
  'chained',
  'in_flight_run',
  'name_taken',
  'record_changed',
] as const;
export const UnmergeRefusalReason = z.enum(UNMERGE_REFUSAL_REASONS);
export type UnmergeRefusalReason = z.infer<typeof UnmergeRefusalReason>;

/**
 * **Why a row stayed where the merge put it.** An unmerge removes only what the
 * merge did, and it never guesses:
 *
 *  - `later_support` — research or notes since the merge cited this finding
 *    under the surviving record's own name, so it stays with that record.
 *  - `spelling_unclear` — a finding that arrived since the merge cannot be told
 *    apart from one about another record joined into the same survivor, or its
 *    source's spelling was not kept.
 *  - `changed_since` — the row has been edited, moved or removed since the
 *    merge, and the later change stands.
 *  - `original_unknown` — the merge did not record enough to put this row back
 *    exactly (a merge written before the record was complete).
 *  - `slot_taken` — putting it back would land on a finding that already exists.
 *  - `pair_question` — a question about these same two records, left answered:
 *    the unmerge has just said they are different.
 */
export const UNMERGE_LEFT_REASONS = [
  'later_support',
  'spelling_unclear',
  'changed_since',
  'original_unknown',
  'slot_taken',
  'pair_question',
] as const;
export const UnmergeLeftReason = z.enum(UNMERGE_LEFT_REASONS);
export type UnmergeLeftReason = z.infer<typeof UnmergeLeftReason>;

const UnmergeRecord = z
  .object({
    entity_id: Uuid,
    display_name: z.string(),
  })
  .strict();

export const UnmergeLeftRow = z
  .object({
    /** The table the row lives in: `fact`, `fact_claim`, `claim`, `decision`, … */
    table: z.string(),
    row_id: z.string(),
    reason: UnmergeLeftReason,
  })
  .strict();
export type UnmergeLeftRow = z.infer<typeof UnmergeLeftRow>;

export const UnmergeResult = z
  .object({
    dry_run: z.boolean(),
    merge_decision_id: Uuid,
    /** The `entity_unmerge` decision. Null on a dry run, which wrote nothing. */
    unmerge_decision_id: Uuid.nullable(),
    /** The record brought back. */
    restored: UnmergeRecord,
    /** The record it had been merged into, under the name it carries now. */
    survivor: UnmergeRecord,
    /**
     * Rows put back, by the merge-ledger action that moved them
     * (`fact_repointed`, `anchor_moved`, …), plus `key_restored`,
     * `twin_restored` and `membership_link_restored` for the steps the
     * decision payload recorded rather than the ledger.
     */
    counts: z.record(z.string(), z.number().int().nonnegative()),
    /** Findings that arrived after the merge under the restored record's spelling, moved back. */
    later_facts_returned: z.number().int().nonnegative(),
    /** Everything left where the merge put it, and why. */
    left_on_survivor: z.array(UnmergeLeftRow),
    /**
     * Findings put back with no vector, because the merge cleared it. The
     * ordinary `embeddings:backfill` does not reach a row with no vector; run it
     * with `--lineage` for these.
     */
    embeddings_to_rebuild: z.array(Uuid),
    /** One plain sentence saying what happened. */
    sentence: z.string(),
  })
  .strict();
export type UnmergeResult = z.infer<typeof UnmergeResult>;
